import React from "react";
import { PauseCircle, ExternalLink, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Aviso mostrado ao cliente quando um formulário público (contact / orcamento /
 * agendamento / diagnostico) está temporariamente suspenso pela administração.
 *
 * Props:
 *  - title (string): título curto (ex: "Em férias")
 *  - message (string): mensagem detalhada
 *  - onClose (fn): fecha o diálogo pai
 *  - onOpenChat (fn, opcional): abre o chat IA
 *  - variant: "dialog" (dentro de modal) | "block" (embutido inline)
 */
export default function SuspendedNotice({
  title = "Serviço em pausa",
  message = "Este serviço está temporariamente indisponível.",
  onClose,
  onOpenChat,
  variant = "dialog",
  testId = "suspended-notice",
}) {
  const handleChat = () => {
    if (typeof onClose === "function") onClose();
    if (typeof onOpenChat === "function") {
      onOpenChat();
    } else {
      // fallback: dispara evento global que o ChatWidget escuta
      try {
        window.dispatchEvent(new CustomEvent("open-chat"));
      } catch {
        /* noop */
      }
    }
  };

  const handleContact = () => {
    if (typeof onClose === "function") onClose();
  };

  const containerCls =
    variant === "dialog"
      ? "px-6 py-8 sm:px-10 sm:py-10 text-center"
      : "rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 via-white to-white p-6";

  return (
    <div className={containerCls} data-testid={testId}>
      <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center">
        <PauseCircle size={28} strokeWidth={2} />
      </div>
      <h3 className="mt-4 font-heading text-xl sm:text-2xl font-bold text-slate-900">
        {title}
      </h3>
      <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed max-w-md mx-auto whitespace-pre-line">
        {message}
      </p>
      <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
        <Link
          to="/contactos"
          onClick={handleContact}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1698AC] hover:bg-[#0F7484] text-white text-sm font-medium transition-colors"
          data-testid={`${testId}-contact`}
        >
          <ExternalLink size={14} /> Contactar-nos
        </Link>
        <button
          type="button"
          onClick={handleChat}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-300 hover:border-[#1698AC] hover:text-[#1698AC] text-slate-700 text-sm font-medium transition-colors"
          data-testid={`${testId}-chat`}
        >
          <MessageCircle size={14} /> Falar com Assistente IA
        </button>
      </div>
    </div>
  );
}
